'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Stack } from '@mui/material';
import { DescriptionOutlined, Link } from '@mui/icons-material';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers';
import { PlayButton, TextButton, MediaSlider, IconButton } from '../..';
import { useTranslations } from 'next-intl';
import {
    useUserId,
    useBreakpoint,
    useImageSizeCalculation,
    useIsOverflowing,
    useToggle
} from '../../../hooks';
import { UploadedMedia, UploadType, isCompact } from '../../../types';
import { Post } from '../../../types/Post';
import { isUploadedImage, isUploadedWeblink, getUploadName, openInNewTab } from '../../../utils';
import { ProjectDetailsCoWorkPostSectionEditPost } from '../create-edit-post/ProjectDetailsCoWorkPostSectionEditPost';
import { ProjectDetailsCoWorkPostCommentSection } from './comment-section/ProjectDetailsCoWorkPostCommentSection';
import {
    FullSizeImageFrame,
    GalleryVideo,
    FullSizeImageContainer,
    FullSizeImage,
    SourceContainer,
    SourceText,
    Gallery,
    ImagesList,
    GalleryItem,
    GalleryImage,
    ArrowContainer,
    PostContainer,
    PostTitle,
    PostText
} from './styles';
import { getLogger } from '../../../logger/logger';

const logger = getLogger('postLogger');

interface Props {
    post: Post;
    refresh: () => void;
    canDeletePosts: boolean;
    canCreatePosts: boolean;
}

enum ScrollDirection {
    Left,
    Right
}

// we can't put this helper function in utils/uploads.ts because we have to make the ReactPlayer check on the client
const uploadIsImageOrVideo = (
    file: UploadedMedia<UploadType>
): file is UploadedMedia<UploadType.Weblink | UploadType.Image> =>
    isUploadedImage(file) || (isUploadedWeblink(file) && ReactPlayer.canPlay(file.url));

type GalleryMedia = UploadedMedia<UploadType.Image | UploadType.Weblink>;

const ProjectDetailsCoWorkPost = ({ post, refresh, canDeletePosts, canCreatePosts }: Props) => {
    const { title, text, author, uploads = [] } = post;
    const t = useTranslations('general');
    const currentUserId = useUserId();
    const isUserAuthor = currentUserId === post.author?.id;

    const breakpoint = useBreakpoint();
    const imagesListRef = useRef<HTMLElement>(undefined);

    const [mediaInFullSize, setMediaInFullSize] = useState<GalleryMedia | undefined>(
        uploads.find(uploadIsImageOrVideo)
    );

    const [indexInSlider, setIndexInSlider] = useState<number>();

    const { imageDimensions, containerRef, onImageLoad, containerSize } = useImageSizeCalculation(
        {}
    );
    useIsOverflowing(imagesListRef, (val) => setShowRightArrow(val));

    const [showLeftArrow, setShowLeftArrow] = useToggle();
    const [showRightArrow, setShowRightArrow] = useToggle();

    const { imagesAndVideos, filesAndLinks } = useMemo(() => {
        const imagesAndVideos: GalleryMedia[] = [];
        const filesAndLinks: UploadedMedia<UploadType.Document | UploadType.Weblink>[] = [];

        uploads.forEach((upload) => {
            if (uploadIsImageOrVideo(upload)) imagesAndVideos.push(upload);
            else
                filesAndLinks.push(
                    upload as UploadedMedia<UploadType.Document | UploadType.Weblink>
                );
        });
        return { imagesAndVideos, filesAndLinks };
    }, [uploads]);

    useEffect(() => {
        const hasVisibleMedia = imagesAndVideos.length > 0;
        const isUpToDate =
            (!mediaInFullSize && !hasVisibleMedia) ||
            (mediaInFullSize &&
                !!imagesAndVideos.find(
                    ({ uniqueMediaId }) => uniqueMediaId === mediaInFullSize.uniqueMediaId
                ));
        if (!isUpToDate) setMediaInFullSize(hasVisibleMedia ? imagesAndVideos[0] : undefined);
    }, [imagesAndVideos, mediaInFullSize, setMediaInFullSize]);

    const openSlider = () => {
        const indexToOpen = imagesAndVideos.findIndex(
            (m) => m.uniqueMediaId === mediaInFullSize?.uniqueMediaId
        );
        if (indexToOpen >= 0) setIndexInSlider(indexToOpen);
        else logger.error('Could not open slider');
    };

    const renderFullSizeMedia = () => {
        if (imagesAndVideos.length === 0) return null;
        const { url, name, description, source } = mediaInFullSize ?? imagesAndVideos[0];
        const isVideo = ReactPlayer.canPlay(url);
        const isCompactLayout = isCompact(breakpoint);
        const calculatedHeight = containerSize.width / (16 / 9);
        const videoWidth = containerSize.height * (16 / 9);

        const maxDimensions = { maxHeight: containerSize.height, maxWidth: containerSize.width };

        return (
            <FullSizeImageFrame
                ref={containerRef}
                sx={{ height: calculatedHeight, maxHeight: 'calc(100vh * 0.3)' }}
                onClick={() => !isCompactLayout && openSlider()}
            >
                {isVideo ? (
                    <GalleryVideo
                        light
                        url={url}
                        width={videoWidth}
                        style={{
                            pointerEvents: isCompactLayout ? 'auto' : 'none',
                            ...maxDimensions
                        }}
                        playIcon={<PlayButton style={{ height: 70, width: 70 }} />}
                    />
                ) : (
                    <FullSizeImageContainer
                        sx={{
                            ...imageDimensions,
                            ...maxDimensions,
                            minHeight: 170
                        }}
                    >
                        <FullSizeImage
                            fill
                            priority
                            src={url}
                            alt={description ?? name ?? url}
                            onLoad={onImageLoad}
                        />
                        {source && (
                            <SourceContainer>
                                <Link />
                                <SourceText>{`${isCompact(breakpoint) ? '' : t('source_prefix')} ${source}`}</SourceText>
                            </SourceContainer>
                        )}
                    </FullSizeImageContainer>
                )}
            </FullSizeImageFrame>
        );
    };

    const handleScroll = (direction: ScrollDirection) => {
        const list = imagesListRef.current;
        if (list) {
            const scrollIncrement = list.offsetWidth; // Scroll by visible width
            if (direction === ScrollDirection.Left) {
                const newScrollPos = list.scrollLeft - scrollIncrement;
                list.scroll({ behavior: 'smooth', left: newScrollPos });
                setShowLeftArrow(newScrollPos > 0);
                setShowRightArrow(true);
            } else {
                const newScrollPos = list.scrollLeft + scrollIncrement;
                list.scroll({ behavior: 'smooth', left: newScrollPos });
                setShowLeftArrow(true);
                setShowRightArrow(newScrollPos < list.scrollWidth - list.clientWidth);
            }
        }
    };

    const renderMediaGallery = () => {
        if (imagesAndVideos.length < 2) return null;
        // items should have a minimum size of 48px and should get slightly bigger by screen size (5% of the container width)
        const minItemHeight = 48;
        const calculatedHeight = containerSize.width * 0.05;
        const height = calculatedHeight > minItemHeight ? calculatedHeight : minItemHeight;
        const width = height * (16 / 9);
        const mediaDimensions = { height, width };

        return (
            <Gallery>
                <ImagesList ref={imagesListRef}>
                    {imagesAndVideos.map((media) => {
                        const { uniqueMediaId, type, url, description, name } = media;
                        const isVideo = type === UploadType.Weblink;
                        const isSelected = mediaInFullSize?.uniqueMediaId === uniqueMediaId;
                        return (
                            <GalleryItem
                                key={uniqueMediaId}
                                isSelected={isSelected}
                                onClick={() => setMediaInFullSize(media)}
                            >
                                {isVideo ? (
                                    <GalleryVideo
                                        light
                                        url={url}
                                        {...mediaDimensions}
                                        playIcon={<PlayButton style={{ height: 35, width: 35 }} />}
                                    />
                                ) : (
                                    <GalleryImage
                                        priority
                                        src={url}
                                        alt={description ?? name ?? url}
                                        {...mediaDimensions}
                                    />
                                )}
                            </GalleryItem>
                        );
                    })}
                </ImagesList>
                {showLeftArrow && (
                    <ArrowContainer arrow={'left'}>
                        <IconButton
                            icon={<ArrowLeftIcon />}
                            onClick={() => handleScroll(ScrollDirection.Left)}
                        />
                    </ArrowContainer>
                )}
                {showRightArrow && (
                    <ArrowContainer arrow={'right'}>
                        <IconButton
                            icon={<ArrowRightIcon />}
                            onClick={() => handleScroll(ScrollDirection.Right)}
                        />
                    </ArrowContainer>
                )}
            </Gallery>
        );
    };

    const renderFilesAndLinks = () => (
        <Stack>
            {filesAndLinks.map(({ uniqueMediaId, type, url, name }) => {
                const displayName = name ?? getUploadName(url, type);
                const icon = type === UploadType.Weblink ? <Link /> : <DescriptionOutlined />;
                return (
                    <TextButton
                        key={uniqueMediaId}
                        icon={icon}
                        label={displayName}
                        onClick={() => openInNewTab(url)}
                        sx={{ width: 'fit-content' }}
                    />
                );
            })}
        </Stack>
    );

    return (
        <PostContainer breakpoint={breakpoint}>
            {(canDeletePosts || isUserAuthor) && (
                <ProjectDetailsCoWorkPostSectionEditPost
                    post={post}
                    refresh={refresh}
                    isUserAuthor={isUserAuthor}
                />
            )}
            {renderFullSizeMedia()}
            {renderMediaGallery()}
            <PostTitle>{title}</PostTitle>
            <PostText>{text}</PostText>
            {renderFilesAndLinks()}
            {author ? (
                <ProjectDetailsCoWorkPostCommentSection
                    post={post}
                    canCreatePost={canCreatePosts}
                />
            ) : null}
            {indexInSlider !== undefined && (
                <MediaSlider
                    open={indexInSlider !== undefined}
                    initialIndex={indexInSlider}
                    media={imagesAndVideos}
                    onClose={() => setIndexInSlider(undefined)}
                />
            )}
        </PostContainer>
    );
};

export { ProjectDetailsCoWorkPost };
